import { getInjectedContent } from '@modules/core/services/injected-content';
import { GlobalMetaTagName } from '@modules/core/services/initial-state';
import { ImgCdnParameters } from './types';
import { featureFlagService } from '@monolith/legacy/services/features';
import { ImgCdnService } from './img-cdn-service';
import { memoize } from '@core/utilities/lodash-utils';

const getImgCdnBaseUrl = () => getInjectedContent(GlobalMetaTagName.CDN) ?? 'img.ankorstore.com';

export const getImgCdnServiceInstance = memoize(() => new ImgCdnService(`https://${getImgCdnBaseUrl()}`, featureFlagService));

export const imgCdnUrl = (filename: string, size: string = null, extraParameters: ImgCdnParameters = {}): string =>
  getImgCdnServiceInstance().imgCdnUrl(filename, size, extraParameters);

export const imgCdnUrlFill = (filename: string, size: string, extraParameters: ImgCdnParameters = {}): string =>
  getImgCdnServiceInstance().imgCdnUrlFill(filename, size, extraParameters);

export const imgCdnUrlCrop = (filename: string, size: string, extraParameters: ImgCdnParameters = {}): string =>
  getImgCdnServiceInstance().imgCdnUrlCrop(filename, size, extraParameters);

export const isImgPathWithCdn = (filename: string): boolean => {
  if (typeof filename !== 'string') {
    return false;
  }
  const value = filename.trim();
  // Consider absolute URLs (http, https) and protocol-relative URLs (//) as already CDN/complete URLs
  return /^(https?:)?\/\//i.test(value);
};

declare global {
  interface Window {
    __CDN_URL?: string;
  }
}

/**
 * Returns the CDN base URL for static assets (from window.__CDN_URL set by Laravel's asset() helper).
 * Falls back to empty string for relative paths if not set.
 */
export const getStaticAssetBaseUrl = (): string => {
  return window.__CDN_URL ?? '';
};

/**
 * Prefixes a static asset path with the CDN URL.
 * Use this for static assets in /images/, /fonts/, etc.
 * @param path - The asset path (e.g., '/images/logo/aria-logo.svg')
 * @returns The full CDN URL (e.g., 'https://cdn.ankorstore.com/ankorstore/images/logo/aria-logo.svg')
 */
export const getStaticAssetUrl = (path: string): string => {
  const baseUrl = getStaticAssetBaseUrl();
  // Remove leading slash from path if base URL ends with slash
  const normalizedPath = baseUrl.endsWith('/') && path.startsWith('/') ? path.slice(1) : path;
  return `${baseUrl}${normalizedPath}`;
};
